import { ChevronLeft, ChevronRight, MoreVertical } from "lucide-react";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCurrency } from "../../../lib/utils";
import { GraphResponse } from "../@types";

type ViewMode = "daily" | "monthly";

interface FinanceGraphProps {
  title: string;
  data: GraphResponse | undefined;
  onFetch: (year: number, month: number) => void;
  type: "income" | "expense";
  isLoading?: boolean;
}

const FinanceGraph: React.FC<FinanceGraphProps> = ({
  title,
  data,
  onFetch,
  type,
  isLoading = false,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("daily");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const chartRef = React.useRef<HTMLDivElement>(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    onFetch(currentDate.getFullYear(), currentDate.getMonth() + 1);
  }, [currentDate, viewMode, onFetch]);

  const getDisplayData = () => {
    if (!data) return [];

    const sourceData = viewMode === "daily" ? data.dailyData : data.monthlyData;

    return sourceData.map((item) => {
      let label = "";

      if (viewMode === "daily" && "date" in item) {
        const [month, day] = item.date.split("-");
        const monthIndex = parseInt(month) - 1;
        label = `${monthNames[monthIndex]?.slice(0, 3) || ""} ${parseInt(day)}`;
      } else if ("month" in item) {
        label = item.month.split(" ")[0];
      }

      return {
        label,
        amount: item.amount,
      };
    });
  };

  const displayData = getDisplayData();

  const getCurrentPeriod = () => {
    if (viewMode === "daily") {
      return `${
        monthNames[currentDate.getMonth()]
      } ${currentDate.getFullYear()}`;
    }
    return currentDate.getFullYear().toString();
  };

  const displayPeriod = data?.currentPeriod || getCurrentPeriod();

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setCurrentDate(date);
      setShowDatePicker(false);
    }
  };

  const handleScroll = (direction: "left" | "right") => {
    if (chartRef.current) {
      const scrollAmount = 200;
      const newScrollLeft =
        chartRef.current.scrollLeft +
        (direction === "right" ? scrollAmount : -scrollAmount);
      chartRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold text-gray-800 mb-1">{label}</p>
          <p className="text-sm text-gray-600">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm flex-1 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-gray-500 text-sm font-medium">{title}</h3>
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md flex items-center gap-2 hover:bg-gray-50"
              >
                {displayPeriod}
                <ChevronRight className="w-4 h-4 transform rotate-90" />
              </button>
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-white">
                  <DatePicker
                    selected={currentDate}
                    onChange={handleDateChange}
                    dateFormat={viewMode === "daily" ? "MMMM yyyy" : "yyyy"}
                    showMonthYearPicker={viewMode === "daily"}
                    showYearPicker={viewMode === "monthly"}
                    inline
                    calendarClassName="shadow-lg border border-gray-200 rounded-lg"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="text-3xl font-bold">
            {formatCurrency(data?.totalAmount || 0)}
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <MoreVertical className="w-5 h-5 text-gray-600" />
          </button>
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <button
                onClick={() => {
                  setViewMode("daily");
                  setShowDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                  viewMode === "daily" ? "bg-gray-50 font-medium" : ""
                }`}
              >
                Daily View
              </button>
              <button
                onClick={() => {
                  setViewMode("monthly");
                  setShowDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                  viewMode === "monthly" ? "bg-gray-50 font-medium" : ""
                }`}
              >
                Monthly View
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className="relative h-64 overflow-x-auto overflow-y-hidden"
        ref={chartRef}
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        ) : (
          <div
            style={{
              minWidth:
                displayData.length > 8
                  ? `${displayData.length * 60}px`
                  : "100%",
              height: "100%",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={displayData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#9ca3af", fontSize: 12 }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#9ca3af", fontSize: 12 }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickLine={false}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
                />
                <Bar
                  dataKey="amount"
                  fill={type === "income" ? "#0E7A99" : "#ef4444"}
                  radius={[20, 20, 20, 20]}
                  maxBarSize={80}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 mt-6">
        <button
          className="p-1 hover:bg-gray-100 rounded"
          onClick={() => handleScroll("left")}
        >
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div className="flex-1 h-1 bg-gray-200 rounded max-w-xs">
          <div className="h-full w-1/3 bg-gray-400 rounded" />
        </div>
        <button
          className="p-1 hover:bg-gray-100 rounded"
          onClick={() => handleScroll("right")}
        >
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

export default FinanceGraph;
